import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './HalloweenThemeOverlay.css';

const TOTAL_MS = 1150;
const MIDPOINT_MS = Math.round(TOTAL_MS * 0.50);

const PARTICLE_COLORS = ['#ff7518', '#7a3fa0', '#7cb342', '#ffd9a8', '#f3e0c8'];

const BatMini = () => (
  <svg viewBox="0 0 48 28" width="1em" height="1em" style={{ display: 'block' }}>
    <path
      d="M24 10c-2-5-9-9-15-8 3 2 4 5 3 8-5-1-10 1-12 5 4-1 7 0 9 2-3 1-5 3-6 6 4-2 8-2 11 0 2 1 3 3 3 5 0-2 1-4 3-5 0 2 1 4 4 5 3-1 4-3 4-5 2 1 3 3 3 5 0-2 1-4 3-5 3-2 7-2 11 0-1-3-3-5-6-6 2-2 5-3 9-2-2-4-7-6-12-5-1-3 0-6 3-8-6-1-13 3-15 8z"
      fill="currentColor"
    />
  </svg>
);

const PumpkinMini = () => (
  <svg viewBox="0 0 40 36" width="1em" height="1em" style={{ display: 'block' }}>
    <rect x="17.5" y="0" width="5" height="8" rx="2" fill="#4a7a3a" />
    <ellipse cx="20" cy="22" rx="18" ry="14" fill="currentColor" />
    <path d="M13 21l3-4 3 4h-6zM24 21l3-4 3 4h-6z" fill="#2a1420" />
    <path d="M13 26q7 4 14 0" stroke="#2a1420" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);

const PARTICLES = (() => {
  const COUNT = 22;
  return Array.from({ length: COUNT }, (_, i) => {
    const angle = (i / COUNT) * 2 * Math.PI;
    const dist = 130 + (i % 4) * 55;
    const kind = i % 3 === 0 ? 'bat' : (i % 3 === 1 ? 'spark' : 'pumpkin');
    return {
      id: i,
      kind,
      glyph: i % 2 ? '✧' : '✦',
      color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist - 24,
      delay: i * 0.022,
      size: 15 + (i % 4) * 4,
    };
  });
})();

function renderParticle(p) {
  if (p.kind === 'bat') return <BatMini />;
  if (p.kind === 'pumpkin') return <PumpkinMini />;
  return p.glyph;
}

export default function HalloweenThemeOverlay({ isRunning, origin, entering, onMidpoint, onDone }) {
  const timersRef = useRef([]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isRunning) return;

    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    const t1 = setTimeout(onMidpoint, MIDPOINT_MS);
    const t2 = setTimeout(onDone, TOTAL_MS + 50);

    timersRef.current = [t1, t2];
  }, [isRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isRunning) return null;

  const { x, y } = origin;
  const waveStyle = {
    '--hw-ox': `${x}px`,
    '--hw-oy': `${y}px`,
  };

  return ReactDOM.createPortal(
    <div className="hw-overlay-root">
      <div
        className={`hw-wave ${entering ? 'hw-wave--to-halloween' : 'hw-wave--to-normal'}`}
        style={waveStyle}
      >
        <div className="hw-ring hw-ring--1" />
        <div className="hw-ring hw-ring--2" />
        <div className="hw-ring hw-ring--3" />
        <div className="hw-ring-sparkles">✦ 🎃 ✦ 🦇 ✦ 🎃 ✦ 🦇 ✦ 🎃 ✦</div>
        <div className="hw-wave__center">
          {entering
            ? <span className="hw-wave__pumpkin">🎃</span>
            : <span className="hw-wave__spark">✦</span>}
        </div>
      </div>

      <AnimatePresence>
        {PARTICLES.map(p => (
          <motion.div
            key={p.id}
            className="hw-particle"
            style={{ left: x, top: y, fontSize: p.size, color: p.color }}
            initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
            animate={{
              x: p.tx,
              y: p.ty,
              scale: [0, 1.5, 0.9, 0],
              opacity: [0, 1, 0.85, 0],
            }}
            transition={{
              duration: 0.95,
              delay: p.delay,
              ease: 'easeOut',
              opacity: { times: [0, 0.12, 0.7, 1] },
              scale: { times: [0, 0.18, 0.65, 1] },
            }}
          >
            {renderParticle(p)}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}
